import {
  PersonPin as PersonPinIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserViewDialog from '../components/modals/UserViewDialog';
import apiService from '../services/api';
import { Meeting } from '../types/meeting.type';

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'scheduled':
      return '#0288d1';
    case 'completed':
      return '#2e7d32';
    case 'cancelled':
      return '#d32f2f';
    case 'rescheduled':
      return '#ed6c02';
    default:
      return '#9e9e9e';
  }
};
const STATUS_OPTIONS = ['scheduled', 'completed', 'cancelled', 'rescheduled'];
const DAY_OPTIONS = [
  { label: 'Day A', value: 'A' },
  { label: 'Day B', value: 'B' },
  { label: 'Day C', value: 'C' },
];

const TIME_SLOTS = [
  { id: '11:30-12:00', label: '11:30 AM - 12 Noon' },
  { id: '12:00-12:30', label: '12 Noon - 12:30 PM' },
  { id: '12:30-01:00', label: '12:30 PM - 1:00 PM' },
  { id: '02:00-02:30', label: '2:00 PM - 2:30 PM' },
  { id: '03:30-04:00', label: '3:30 PM - 4:00 PM' },
  { id: '05:30-06:00', label: '5:30 PM - 6:00 PM' },
];

const MeetingList: React.FC = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  console.log('meetings', meetings);
  const [expanded, setExpanded] = useState<number | false>(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState<'all' | 'A' | 'B' | 'C'>('all');

  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [tableNumber, setTableNumber] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedDay, setSelectedDay] = useState<'A' | 'B' | 'C'>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<any>(null);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiService.getMeetings({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        dayCode: dayFilter === 'all' ? undefined : dayFilter,
      });

      setMeetings(res.data || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      setError('Failed to load meetings');
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [page, rowsPerPage, search, statusFilter, dayFilter]);

  const formatTime = (time: string) => time.slice(0, 5);

  return (
    <>
      <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Meetings
          </Typography>
          {!loading && !error && meetings.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          )}
        </Box>

        {/* Search */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <TextField
            size="small"
            placeholder="Search by exhibitor / delegate / email / name"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: '100%', sm: 360 } }}
          />

          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter || ''}
            onChange={e => {
              setStatusFilter(e.target.value || null);
              setPage(0);
            }}
            sx={{ width: 180 }}
          >
            {STATUS_OPTIONS.map(status => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label="All"
            clickable
            color={dayFilter === 'all' ? 'primary' : 'default'}
            onClick={() => {
              setDayFilter('all');
              setPage(0);
            }}
          />

          <Chip
            label="Day A (23rd March)"
            clickable
            color={dayFilter === 'A' ? 'primary' : 'default'}
            onClick={() => {
              setDayFilter('A');
              setPage(0);
            }}
          />

          <Chip
            label="Day B (24th March)"
            clickable
            color={dayFilter === 'B' ? 'primary' : 'default'}
            onClick={() => {
              setDayFilter('B');
              setPage(0);
            }}
          />

          <Chip
            label="Day C (25th March)"
            clickable
            color={dayFilter === 'C' ? 'primary' : 'default'}
            onClick={() => {
              setDayFilter('C');
              setPage(0);
            }}
          />
        </Stack>

        {/* Loading */}
        {loading && (
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography>Loading meetings...</Typography>
          </Box>
        )}

        {/* Empty State */}
        {!loading && meetings.length === 0 && (
          <Box
            sx={{
              mt: 6,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <SearchOffIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" fontWeight={500}>
              No meetings found
            </Typography>
            <Typography variant="body2">
              There are no meetings available at the moment.
            </Typography>
          </Box>
        )}

        {/* Meeting List */}
        {!loading && !error && meetings.length > 0 && (
          <Stack spacing={2}>
            {meetings.map(meeting => (
              <Accordion
                key={meeting.id}
                expanded={expanded === meeting.id}
                onChange={(_, isExpanded) =>
                  setExpanded(isExpanded ? meeting.id : false)
                }
                sx={{
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                }}
              >
                {/* Summary */}
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={2}
                    width="100%"
                    alignItems="center"
                  >
                    {/* Status + text grouped */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flex={1}
                    >
                      <Tooltip
                        title={
                          meeting.status.charAt(0).toUpperCase() +
                          meeting.status.slice(1)
                        }
                      >
                        <Box
                          sx={{
                            width: 15,
                            height: 15,
                            borderRadius: '50%',
                            bgcolor: getStatusColor(meeting.status),
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>

                      <Stack>
                        <Typography fontWeight={500}>
                          {meeting.delegate?.delegateDetails?.fullName || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          With{' '}
                          {meeting.exhibitor?.exhibitorDetails?.companyName ||
                            '-'}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Right action */}
                    <Chip
                      label="Assign Table"
                      color="secondary"
                      clickable
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedMeeting(meeting);
                        setTableNumber(meeting.tableNumber || '');
                        setOpenAssignModal(true);
                      }}
                    />
                  </Stack>
                </AccordionSummary>

                {/* Details */}
                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AccessTimeIcon fontSize="small" />
                      <Typography variant="body2">
                        Day {meeting.timeSlot?.dayCode || '-'} •{' '}
                        {meeting.timeSlot?.startTime
                          ? formatTime(meeting.timeSlot.startTime)
                          : '--'}{' '}
                        –{' '}
                        {meeting.timeSlot?.endTime
                          ? formatTime(meeting.timeSlot.endTime)
                          : '--'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <LocationOnIcon fontSize="small" />
                      <Typography variant="body2">
                        Booth: {meeting.boothNumber || 'N/A'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PersonPinIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': { color: 'primary.main' },
                        }}
                        onClick={() => {
                          setViewingUser(meeting.delegate);
                          setViewDialogOpen(true);
                        }}
                      >
                        Delegate:{' '}
                        {meeting.delegate?.delegateDetails?.fullName || '-'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <StoreIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': { color: 'primary.main' },
                        }}
                        onClick={() =>
                          navigate(
                            `/exhibitors/${meeting.exhibitor.exhibitorDetails.id}`
                          )
                        }
                      >
                        Exhibitor:{' '}
                        {meeting.exhibitor?.exhibitorDetails?.companyName ||
                          '-'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PublicIcon fontSize="small" />
                      <Typography variant="body2">
                        Country:{' '}
                        {meeting.delegate?.delegateDetails?.country || ''}
                      </Typography>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Table</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Delegate:{' '}
            <b>{selectedMeeting?.delegate?.delegateDetails?.fullName || '-'}</b>
          </Typography>

          <Grid container spacing={2}>
            {/* Table Number - FULL WIDTH */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Table Number"
                value={tableNumber}
                onChange={e => setTableNumber(e.target.value)}
                placeholder="Enter table number"
              />
            </Grid>

            {/* Day Dropdown - HALF */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Day"
                value={selectedDay}
                onChange={e =>
                  setSelectedDay(e.target.value as 'A' | 'B' | 'C')
                }
              >
                {DAY_OPTIONS.map(day => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Time Dropdown - HALF */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Time Slot"
                value={selectedTimeSlot}
                onChange={e => setSelectedTimeSlot(e.target.value)}
              >
                {TIME_SLOTS.map(slot => (
                  <MenuItem key={slot.id} value={slot.id}>
                    {slot.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenAssignModal(false)}> Cancel </Button>

          <Button
            variant="contained"
            onClick={() => {
              setOpenAssignModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <UserViewDialog
        open={viewDialogOpen}
        user={viewingUser}
        onClose={() => {
          setViewDialogOpen(false);
          setViewingUser(null);
        }}
      />
    </>
  );
};

export default MeetingList;
